import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Calendar, Clock, ArrowRight, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const blogPosts = [
  {
    id: 'fridge-humming-noise',
    slug: 'why-is-my-refrigerator-making-humming-noise',
    title: 'Why is my Refrigerator Making a Humming Noise?',
    excerpt: 'If your fridge is humming loudly in Vaughan or Toronto, it\'s usually a dusty condenser coil or a faulty evaporator fan.',
    content: `If your fridge is humming loudly in Vaughan or Toronto, it's usually a dusty condenser coil or a faulty evaporator fan. While cleaning coils is a DIY fix, fan issues require a pro. 

Infinity Appliances provides 24/7 fridge diagnostics across the GTA to save your groceries from spoiling.

**Common Causes of Refrigerator Humming:**
1. **Dusty Condenser Coils** - Over time, dust and pet hair can accumulate on the condenser coils located at the back or bottom of your refrigerator. This forces the compressor to work harder, creating a louder humming sound.

2. **Faulty Evaporator Fan** - The evaporator fan circulates cold air throughout the fridge. If it's damaged or obstructed, you'll hear an unusual humming or buzzing noise.

3. **Compressor Issues** - An aging or failing compressor often makes a loud humming noise as it struggles to maintain temperature.

4. **Ice Buildup** - Excessive frost around the evaporator coils can cause the fan to work harder, creating additional noise.

**When to Call a Professional:**
- If the humming is accompanied by temperature fluctuations
- If your food is spoiling faster than usual
- If the noise persists after cleaning the condenser coils
- If you notice any ice buildup inside the freezer

Don't wait until your groceries spoil! Call Infinity Appliances Repair at 647-919-6893 for same-day fridge diagnostics in Toronto, Vaughan, and the entire GTA.`,
    image: 'https://images.unsplash.com/photo-1769739132671-ac41c439d51e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    author: 'Infinity Appliances Team',
    date: 'December 10, 2025',
    readTime: '5 min read',
    keywords: ['Refrigerator repair Toronto', 'fridge troubleshooting GTA'],
  },
  {
    id: 'dishwasher-failure-signs',
    slug: '5-signs-your-dishwasher-is-about-to-fail',
    title: '5 Signs Your Dishwasher is About to Fail',
    excerpt: 'Standing water or spotted dishes? These are early signs your pump or heating element is failing.',
    content: `Standing water or spotted dishes? These are early signs your pump or heating element is failing. Catching these in North York or Markham before they leak can save you thousands in water damage.

We offer same-day dishwasher inspections to keep your kitchen running smoothly.

**5 Warning Signs Your Dishwasher Needs Attention:**

**1. Standing Water at the Bottom**
If water remains pooled at the bottom of your dishwasher after a cycle, it's a clear sign of drainage problems. This could be caused by a clogged filter, blocked drain hose, or failing pump.

**2. Dishes Come Out Dirty or Spotted**
When your dishwasher stops cleaning effectively, it often indicates issues with the spray arms, water inlet valve, or heating element. Hard water buildup can also contribute to this problem.

**3. Strange Noises During Operation**
Grinding, humming, or rattling sounds typically point to motor problems, pump issues, or foreign objects caught in the spray arms.

**4. Water Leaking Onto the Floor**
Leaks can stem from worn door gaskets, loose connections, or cracked hoses. Even small leaks should be addressed immediately to prevent water damage to your kitchen floor and cabinets.

**5. Dishes Aren't Drying Properly**
If dishes remain wet after the drying cycle, the heating element may be failing. This is especially common in older dishwashers.

**Prevention Tips:**
- Clean the filter monthly
- Run hot water before starting a cycle
- Use rinse aid to prevent water spots
- Inspect door gaskets for wear

If you notice any of these signs, don't wait for a complete breakdown. Call Infinity Appliances Repair at 647-919-6893 for same-day dishwasher service in Richmond Hill, Markham, North York, and throughout the GTA.`,
    image: 'https://images.unsplash.com/photo-1758631130778-42d518bf13aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    author: 'Infinity Appliances Team',
    date: 'December 5, 2025',
    readTime: '6 min read',
    keywords: ['Dishwasher repair Richmond Hill', 'appliance maintenance Toronto'],
  },
];

export const BlogPage = () => {
  useEffect(() => {
    document.title = 'Appliance Repair Tips & Guides | Infinity Appliances Blog';
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Expert appliance repair tips for GTA homeowners. Learn about refrigerator troubleshooting, dishwasher maintenance, and when to call a professional.';

    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="blog-page">
      {/* Hero Section */}
      <section className="py-20" style={{ background: '#1A2B4C' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span 
              className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-6"
              style={{ background: 'rgba(255, 107, 0, 0.2)', color: '#FF6B00' }}
            >
              Expert Tips & Guides
            </span>
            <h1 
              className="text-4xl sm:text-5xl font-extrabold text-white mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Appliance Repair Blog
            </h1>
            <p className="text-xl text-white/80">
              Helpful tips and troubleshooting guides from our GTA appliance repair experts.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {blogPosts.map((post) => (
              <Card 
                key={post.id}
                className="blog-card overflow-hidden border-0 shadow-lg"
                data-testid={`blog-post-${post.id}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 text-sm text-[#475569] mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {post.readTime}
                    </span>
                  </div>
                  <h2 
                    className="text-xl font-bold mb-3"
                    style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}
                  >
                    {post.title}
                  </h2>
                  <p className="text-[#475569] mb-4 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.keywords.map((keyword) => (
                      <span 
                        key={keyword}
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{ background: 'rgba(26, 43, 76, 0.1)', color: '#1A2B4C' }}
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                  <a
                    href={`#${post.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(post.slug)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="inline-flex items-center gap-2 font-semibold hover:gap-3 transition-all"
                    style={{ color: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }}
                    data-testid={`blog-read-more-${post.id}`}
                  >
                    <span>Read Full Article</span>
                    <ArrowRight size={16} />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Full Articles */}
      {blogPosts.map((post) => (
        <section 
          key={post.id}
          id={post.slug}
          className="py-20 border-t border-gray-100"
          data-testid={`blog-article-${post.id}`}
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <article>
              <header className="mb-8">
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-4"
                  style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}
                >
                  {post.title}
                </h2>
                <div className="flex items-center gap-4 text-[#475569]">
                  <span className="flex items-center gap-2">
                    <User size={16} />
                    {post.author}
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar size={16} />
                    {post.date}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock size={16} />
                    {post.readTime}
                  </span>
                </div>
              </header>

              <img
                src={post.image}
                alt={post.title}
                className="w-full h-64 object-cover rounded-xl mb-8"
              />

              <div className="prose prose-lg max-w-none">
                {post.content.split('\n\n').map((paragraph, idx) => {
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return (
                      <h3 
                        key={idx}
                        className="text-xl font-bold mt-8 mb-4"
                        style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}
                      >
                        {paragraph.replace(/\*\*/g, '')}
                      </h3>
                    );
                  }
                  if (paragraph.includes('**')) {
                    return (
                      <p key={idx} className="text-[#475569] leading-relaxed mb-4">
                        {paragraph.split('**').map((part, i) => 
                          i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                        )}
                      </p>
                    );
                  }
                  if (paragraph.startsWith('- ') || paragraph.match(/^\d\./)) {
                    const items = paragraph.split('\n').filter(item => item.trim());
                    return (
                      <ul key={idx} className="list-disc list-inside space-y-2 mb-4 text-[#475569]">
                        {items.map((item, i) => (
                          <li key={i}>{item.replace(/^[-\d.]\s*/, '')}</li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p key={idx} className="text-[#475569] leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  );
                })}
              </div>

              {/* Article CTA */}
              <div className="mt-12 p-6 rounded-xl" style={{ background: 'rgba(255, 107, 0, 0.1)' }}>
                <h4 
                  className="text-xl font-bold mb-2"
                  style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}
                >
                  Need Professional Help?
                </h4>
                <p className="text-[#475569] mb-4">
                  Our certified technicians are available for same-day service across the GTA.
                </p>
                <a
                  href="tel:647-919-6893"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white transition-transform hover:scale-105"
                  style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }}
                  data-testid={`blog-cta-${post.id}`}
                >
                  <Phone size={18} />
                  <span>Call 647-919-6893</span>
                </a>
              </div>
            </article>
          </div>
        </section>
      ))}
    </main>
  );
};

export default BlogPage;
